import type { VersionedWording } from '@/lib/versioned-labels';

/**
 * How the versioned layer names an experience, everywhere it has to (refonte
 * modulaire §9, §15).
 *
 * The fourth of these files, and deliberately a copy rather than a template
 * over a noun: French articles and agreements are not a concatenation, and the
 * referents differ from one object to the next. A criterion is cited by
 * PROTOCOLES; a persona is chosen by an EXPÉRIENCE; a protocol is played by an
 * expérience; an experience is played by a LIEN (§4, H25), so every sentence of
 * the retirement names links and agrees in the feminine around a feminine
 * subject - « Elle n'est plus jouable par un lien. Ceux qui la jouent... ».
 * `developmentHint` names links too: the sentence of the maquette
 * `EnregistrerVersion`, word for word (issue #155).
 *
 * Like its three neighbours, this file is a `.tsx` and holds no component: the
 * two hints of the retirement are JSX for the reason `criterion-wording.tsx`
 * argues at length - a name spliced into a template is ONE text node where the
 * prose around it is its own, Chrome measures each node separately, and the two
 * shapes land the tail of the line a third of a pixel apart.
 *
 * `saveHintNew` is carried here as well, and it is SHORTER than the layer's
 * default rather than longer: an experience is born from the template of §5.4
 * (`newExperienceContent`), so its pages, its protocol page and its theme are
 * already legal on the first paint. The only thing a new experience can be
 * missing is its name, and the default sentence - « le nom et la direction » -
 * would have sent somebody hunting for a field this screen does not have.
 *
 * The two prefixes are what every `data-testid` of the screen is built from,
 * which is why they are spelled in the singular then the plural: `experience-carte`
 * and `experiences-grille` are what `experiences.spec.ts` and
 * `screenshots-experiences.mjs` read, and a mark nobody can type is a mark
 * nobody can assert on. They are UNACCENTED, like the three before them: a
 * `data-testid` is code.
 */
export const EXPERIENCE_WORDING: VersionedWording = {
  kind: 'experience',
  entity: 'experience',
  list: 'experiences',
  noun: ['expérience', 'expériences'],
  newTitle: 'Nouvelle expérience',
  createLabel: 'Créer une nouvelle expérience',
  saveHintNew: 'Actif dès que le nom est rempli.',
  developmentHint: () =>
    'Une expérience n’est jouée que par des liens : ce sont les seuls référents qui peuvent apparaître ici.',
  subject: (name) => `Expérience « ${name} »`,
  retirement: {
    panelTitle: 'Retirer cette expérience',
    retiredTitle: 'Expérience retirée',
    confirmLabel: 'Retirer l’expérience',
    panelHint: (name) => (
      <>
        Elle n’est plus proposée aux liens. Ceux qui la jouent gardent un avertissement « remplacer
        l’expérience {name} » jusqu’au remplacement. Restauration possible depuis l’historique.
      </>
    ),
    retiredHint: (name, day) => (
      <>
        Retirée le {day}. Elle n’est plus proposée aux liens, et les liens qui la jouent portent
        l’avertissement « remplacer l’expérience {name} » jusqu’au remplacement. Les sessions
        passées gardent leur gel.
      </>
    ),
    warningTitle: (name) => `Chacun gardera « remplacer l’expérience ${name} »`,
  },
  refusal: {
    nameTaken: 'Ce nom est déjà porté par une autre expérience. Choisissez-en un autre.',
    nameTakenBy: (name) =>
      `« ${name} » porte déjà ce nom. Choisissez-en un autre, ou renommez d’abord l’autre expérience.`,
    retired: 'Cette expérience est retirée. Restaurez-la avant de la modifier.',
  },
};
